/**
 * Core-100 R4 wave 3 (CORE-AZ Prov / D) — is the reminder dispatcher actually running?
 *
 * Reads `reminder_dispatch_health(shop)` (managers/owners only; migration 20270206024600): the heartbeat the
 * `reminder-dispatch` cron records on every run, and this shop's reminders that are overdue and still queued.
 * "Sending on" (ReminderDispatchControl) is the shop's SETTING; this is whether anything is being SENT. A failed read
 * is said as a failed read — never as "running".
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Activity } from 'lucide-react';

export interface ReminderDispatchHealth {
  state: 'alive' | 'error' | 'stale' | 'never_ran';
  expected_interval_minutes: number;
  last_beat_at: string | null;
  minutes_since_beat: number | null;
  last_outcome: 'nothing_due' | 'invoked' | 'error' | null;
  last_invoked_at: string | null;
  last_error: string | null;
  dispatch_enabled: boolean;
  overdue_queued: number;
  oldest_overdue_send_at: string | null;
}

/** The one sentence a manager reads (exported for the contract test). */
export function describeDispatcherHealth(h: ReminderDispatchHealth): { tone: 'ok' | 'warn'; text: string } {
  const ago = h.minutes_since_beat === null ? '' : h.minutes_since_beat === 0 ? 'less than a minute ago' : `${h.minutes_since_beat} minute${h.minutes_since_beat === 1 ? '' : 's'} ago`;
  const overdue = h.overdue_queued > 0
    ? ` ${h.overdue_queued} reminder${h.overdue_queued === 1 ? ' is' : 's are'} overdue and still waiting to be sent.`
    : '';
  switch (h.state) {
    case 'never_ran':
      return { tone: 'warn', text: `The reminder sender has never run, so no reminder has been sent.${overdue}` };
    case 'stale':
      return { tone: 'warn', text: `The reminder sender last ran ${ago} — it should run every ${h.expected_interval_minutes} minutes. Reminders are not going out.${overdue}` };
    case 'error':
      return { tone: 'warn', text: `The reminder sender's last run (${ago}) failed: ${h.last_error ?? 'unknown error'}.${overdue}` };
    default:
      return { tone: h.overdue_queued > 0 ? 'warn' : 'ok', text: `The reminder sender is running (last run ${ago}).${overdue}` };
  }
}

export function ReminderDispatcherHealth({ shopId }: { shopId: string }) {
  const [health, setHealth] = useState<ReminderDispatchHealth | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('reminder_dispatch_health' as never, { p_shop_id: shopId } as never);
    const rows = data as unknown;
    const row = Array.isArray(rows) ? (rows[0] as ReminderDispatchHealth | undefined) : undefined;
    if (error || !row) {
      setFailed(true);
      setHealth(null);
      return;
    }
    setFailed(false);
    setHealth({ ...row, overdue_queued: Number(row.overdue_queued ?? 0) });
  }, [shopId]);

  useEffect(() => { void load(); }, [load]);

  if (failed) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="reminder-dispatcher-health-unknown">
        Whether the reminder sender is running could not be checked just now.
      </p>
    );
  }
  if (!health) return null;
  const { tone, text } = describeDispatcherHealth(health);
  if (tone === 'ok') {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="reminder-dispatcher-health" data-state={health.state}>
        <Activity className="h-4 w-4" /> {text}
      </p>
    );
  }
  return (
    <Alert variant="destructive" data-testid="reminder-dispatcher-health" data-state={health.state}>
      <AlertDescription>{text}</AlertDescription>
    </Alert>
  );
}
